import {pointerUtils, type CreateExtArgs} from '@wix/document-manager-core'
import type {CompRef, Pointer} from '@wix/document-services-types'
import {displayedOnlyStructureUtil} from '@wix/santa-core-utils'
import _ from 'lodash'
import {getComponentType} from '../../../utils/dalUtils'
import type {ComponentsMetadataAPI} from '../../componentsMetadata/componentsMetadata'
import type {DataModelExtensionAPI} from '../../dataModel/dataModel'
import type {SchemaExtensionAPI} from '../../schema/schema'
import type {ScopesExtensionAPI} from '../../scopes'
import type {StructureExtensionAPI} from '../../structure'
import {BASE_PROPS_SCHEMA_TYPE, DATA_TYPES, MASTER_PAGE_ID, VIEW_MODES} from '../../../constants/constants'
import {getItemQueryId} from '../../../utils/inflationUtils'
import {getComponentConnections, getComponentData} from './dataQuerying'
import type {ComponentRemovalEvents} from './types'
const {isRefPointer} = displayedOnlyStructureUtil

const getPropertyTypeForComponent = (createExtArgs: CreateExtArgs, compType: string): string => {
    const {schemaAPI} = createExtArgs.extensionAPI as SchemaExtensionAPI
    const compDef = schemaAPI.getDefinition(compType)
    return compDef?.propertyType || _.find(compDef?.propertyTypes) || BASE_PROPS_SCHEMA_TYPE
}

const setGhostCollapsed = (createExtArgs: CreateExtArgs, propPointer: Pointer): void => {
    const {dal} = createExtArgs
    const stored = dal.get(propPointer)
    if (stored && !stored.ghost) {
        dal.set(propPointer, {...stored, ghost: 'COLLAPSED'})
    }
}

const mergeGhostIntoProperties = (createExtArgs: CreateExtArgs, ptr: CompRef): void => {
    const {dal, pointers, extensionAPI} = createExtArgs
    const {dataModel} = extensionAPI as DataModelExtensionAPI

    const existingProps = dataModel.components.getItem(ptr, DATA_TYPES.prop)
    const ptrCompType = getComponentType(dal, ptr)
    const propType = getPropertyTypeForComponent(createExtArgs, ptrCompType)

    if (existingProps) {
        const propsToSet = {...existingProps, type: existingProps.type || propType}
        const propsPointer = dataModel.components.addItem(ptr, DATA_TYPES.prop, propsToSet)
        setGhostCollapsed(createExtArgs, propsPointer)
    } else if (isRefPointer(ptr)) {
        const conventionId = getItemQueryId(ptr, DATA_TYPES.prop)
        const pageId = pointers.structure.getPageOfComponent(ptr)?.id || MASTER_PAGE_ID
        const addedPointer = dataModel.addItem({type: propType}, DATA_TYPES.prop, pageId, conventionId)
        setGhostCollapsed(createExtArgs, addedPointer)
    } else {
        const propsPointer = dataModel.components.addItem(ptr, DATA_TYPES.prop, {type: propType})
        setGhostCollapsed(createExtArgs, propsPointer)
    }
}

const mergeGhostIntoMobileProperties = (
    createExtArgs: CreateExtArgs,
    desktopPtr: CompRef,
    mobilePtr: CompRef
): void => {
    const {dal, pointers, extensionAPI} = createExtArgs
    const {dataModel} = extensionAPI as DataModelExtensionAPI

    const mobilePropId = dataModel.components.getComponentDataItemId(mobilePtr, DATA_TYPES.prop)
    const pageId = pointers.structure.getPageOfComponent(mobilePtr)?.id || MASTER_PAGE_ID

    if (!mobilePropId) {
        return
    }

    const propPointer = pointers.data.getPropertyItem(mobilePropId, pageId)
    const existingProp = dal.get(propPointer)

    if (existingProp) {
        dal.set(propPointer, {...existingProp, ghost: 'COLLAPSED'})
    } else {
        const desktopProps = dataModel.components.getItem(desktopPtr, DATA_TYPES.prop)
        const propType = getPropertyTypeForComponent(createExtArgs, getComponentType(dal, desktopPtr))
        const baseProps = desktopProps ? {...desktopProps} : {type: propType}
        const propsToCreate = {...baseProps, ghost: 'COLLAPSED'}
        const addedPointer = dataModel.addItem(propsToCreate, DATA_TYPES.prop, pageId, mobilePropId)
        setGhostCollapsed(createExtArgs, addedPointer)
    }
}

const isReferredRoot = (createExtArgs: CreateExtArgs, compPtr: Pointer): boolean => {
    const {pointers, extensionAPI} = createExtArgs
    const scopesApi = (extensionAPI as ScopesExtensionAPI).scopes
    if (scopesApi?.extractScopeFromPointer) {
        const parentPtr = pointers.structure.getParent(compPtr)
        if (!parentPtr) {
            const scopePointer = scopesApi.extractScopeFromPointer(compPtr)
            const ownerPointer = scopePointer && scopesApi.getScopeOwner(scopePointer)
            return !isRefPointer(ownerPointer)
        }
        return !isRefPointer(parentPtr)
    }
    const parentPtr = pointers.structure.getParent(compPtr)
    return !isRefPointer(parentPtr)
}

export const shouldRemoveAsGhost = (createExtArgs: CreateExtArgs, compPtr: Pointer): boolean => {
    const {componentsMetadata} = createExtArgs.extensionAPI as ComponentsMetadataAPI
    if (componentsMetadata.getMetadataValue(compPtr, 'hideAsGhost')) {
        return true
    }
    if (isRefPointer(compPtr) && !isReferredRoot(createExtArgs, compPtr)) {
        return true
    }
    return false
}

export const ghostifyComponent = (
    createExtArgs: CreateExtArgs,
    events: ComponentRemovalEvents,
    compPtr: CompRef,
    callback: VoidFunction = _.noop
): void => {
    const {dal, pointers, extensionAPI, eventEmitter} = createExtArgs
    const {componentsMetadata} = extensionAPI as ComponentsMetadataAPI
    const {mobile} = extensionAPI as StructureExtensionAPI

    let resolvedPointer: Pointer = compPtr
    if (componentsMetadata.shouldBeRemovedByParent(resolvedPointer)) {
        resolvedPointer = componentsMetadata.resolveParentPointer(resolvedPointer) as Pointer
    }
    const pointerToGhostify = pointerUtils.getRepeatedItemPointerIfNeeded(resolvedPointer) as CompRef
    const compType = getComponentType(dal, pointerToGhostify)
    const copyDataItem = getComponentData(createExtArgs, pointerToGhostify)
    const componentConnections = getComponentConnections(createExtArgs, pointerToGhostify)

    mergeGhostIntoProperties(createExtArgs, pointerToGhostify)

    if (mobile.isMobileNamespaceSupported() && pointerToGhostify.type !== VIEW_MODES.MOBILE) {
        const mobilePtr = pointers.structure.getMobilePointer(pointerToGhostify)
        mergeGhostIntoMobileProperties(createExtArgs, pointerToGhostify, mobilePtr as CompRef)
    }

    eventEmitter.emit(
        events.AFTER_GHOSTIFY,
        pointerToGhostify,
        compType,
        undefined,
        undefined,
        undefined,
        copyDataItem,
        undefined,
        componentConnections
    )

    callback()
}
